import {
  Collection,
  Loadable,
  Model,
  ModelAttributes,
  ModelDescription,
  collection,
  model,
  modelConfig,
  reference,
} from "@mixitone/oom";
import { isNil, isPresent, omit } from "@mixitone/util";
import TournamentCompetition from "./TournamentCompetition";
import TournamentPlayer from "./TournamentPlayer";
import TournamentRegistration from "./TournamentRegistration";
import { setAccountId } from "./concerns/SetAccountId";

const config = modelConfig(
  {
    account_id: {
      type: "string",
    },
    club_id: {
      type: "string",
    },
    created_at: {
      type: "datetime",
    },
    eliminated: {
      type: "boolean",
    },
    format_data: {
      type: "json",
      jsonType: {} as Record<string, any>,
    },
    is_deleted: {
      type: "boolean",
    },
    last_match_completed_at: {
      type: "datetime",
    },
    name: {
      type: "string",
    },
    seed: {
      type: "number",
    },
    tournament_competition_id: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
    withdrawn: {
      type: "boolean",
    },
  },
  {
    registrations: collection(() => TournamentRegistration as any, "tournament_entry_id"),
  },
  {
    competition: reference(() => TournamentCompetition as any, "tournament_competition_id"),
    registration: reference(() => TournamentRegistration as any, "registration_id"),
  },
);

interface TournamentEntry extends ModelAttributes<typeof config> {
  competition: Loadable<TournamentCompetition>;
  registrations: Collection<TournamentRegistration>;
}

@setAccountId
@model("tournament_entries", config)
class TournamentEntry extends Model<typeof config> {
  static readonly modelDescription: ModelDescription = {
    description:
      "A tournament entry represents a player or group of players in a competition. " +
      "For singles: one player per entry. For doubles/mixed: two players per entry. " +
      "The name is automatically generated from player names (e.g., 'John / Jane' for doubles). " +
      "Optional seeding (1-16) determines entry's starting position in the bracket. " +
      "Higher seeds (1 being highest) are typically given to stronger players/pairs." +
      "Group is the group this entry belongs to, in the format 'Group 1' etc. Group is only applicable for RoundRobin format competitions. Set to null to clear the group.",
    availableFields: [
      ...Object.keys(omit(config.fields, ["name"])),
      "isCheckedIn",
      "names",
      "shortNames",
      "group",
      "division",
    ],
    relationships: {
      competition: "tournament_competitions where id equals this tournament_competition_id",
      registrations: "tournament_registrations where tournament_entry_id equals this id",
      division: "tournament_divisions where id equals this division_id",
    },
  };
  get validRegistrations() {
    if (isNil(this.registrations)) {
      throw new Error("registrations is not loaded");
    }
    if (!this.competition.loaded) {
      throw new Error("competition is not loaded");
    }
    return !this.withdrawn && this.registrations.length === this.competition.requiredPlayersPerEntry;
  }

  get isCheckedIn() {
    if (isNil(this.players)) {
      throw new Error("players is not loaded");
    }
    return this.players.every((p) => p.isCheckedIn);
  }

  get players() {
    if (!this.registrations.loaded) {
      throw new Error("registrations is not loaded");
    }
    return this.registrations
      .map((r) => r.player as TournamentPlayer)
      .filter(isPresent)
      .toSorted((a, b) => {
        const byName = (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
        if (byName !== 0) return byName;
        return (a.id || "").localeCompare(b.id || "");
      });
  }

  get names() {
    if (!this.registrations.loaded) return [];
    return this.players.map((p) => p.name);
  }

  get shortNames() {
    if (!this.registrations.loaded) return [];
    return this.players.map((p) => p.shortName);
  }

  get name() {
    if (!this.registrations.loaded) return "";
    return this.shortNames.join(" & ");
  }

  get group() {
    return this.readJsonAttributeKey("format_data", "group") as string | undefined;
  }

  set group(value: string | undefined | null) {
    const previousGroup = this.readJsonAttributeKey("format_data", "group") as string | undefined;
    this.writeJsonAttributeKey("format_data", "group", value);
    console.log("[GroupWrite][TournamentEntry.set group]", {
      entryId: this.id,
      competitionId: this.tournament_competition_id,
      previousGroup,
      nextGroup: this.readJsonAttributeKey("format_data", "group"),
    });
  }

  get division() {
    return this.readJsonAttributeKey("format_data", "division") as number | undefined;
  }

  set division(value: number | undefined | null) {
    this.writeJsonAttributeKey("format_data", "division", value);
  }

  /**
   * Set the players for this entry. This will remove any existing registrations
   * for the same players in the same competition.
   */
  async setPlayers(playerIds: string[]) {
    await this.registrations.reload();

    const toDelete = this.registrations.filter((r) => !playerIds.includes(r.tournament_player_id!));
    const toAdd = playerIds.filter((p) => !this.registrations.some((r) => r.tournament_player_id === p));

    let existingPlayerIds = new Set();
    for (const registration of this.registrations) {
      if (existingPlayerIds.has(registration.tournament_player_id)) {
        toDelete.push(registration);
      }
      existingPlayerIds.add(registration.tournament_player_id);
    }

    for (const registration of toDelete) {
      await registration.destroy();
      this.registrations.removeById(registration.id!);
    }

    for (const player of toAdd) {
      const registration = new TournamentRegistration({
        account_id: this.account_id,
        club_id: this.club_id,
        tournament_competition_id: this.tournament_competition_id,
        tournament_entry_id: this.id,
        tournament_player_id: player,
      });
      await registration.save();
      this.registrations.add(registration);
    }

    if (playerIds.length === 0) return;

    // Remove other registrations for the same players for the same competition
    const otherRegistrations = await TournamentRegistration.query()
      .eq("tournament_competition_id", this.tournament_competition_id)
      .neq("tournament_entry_id", this.id)
      .in("tournament_player_id", playerIds)
      .all();
    const leftoverEntryIds = [
      ...new Set(
        otherRegistrations
          .map((registration) => registration.tournament_entry_id)
          .filter((id): id is string => !!id),
      ),
    ];

    await TournamentRegistration.query()
      .eq("tournament_competition_id", this.tournament_competition_id)
      .neq("tournament_entry_id", this.id)
      .in("tournament_player_id", playerIds)
      .deleteAll();

    for (const entryId of leftoverEntryIds) {
      await TournamentEntry.destroyIfNoRegistrations(entryId);
    }
  }

  /**
   * Soft-delete the entry when it has no remaining registrations.
   */
  static async destroyIfNoRegistrations(entryId: string) {
    const remaining = await TournamentRegistration.query().eq("tournament_entry_id", entryId).all();
    if (remaining.length > 0) return;

    const entry = await TournamentEntry.query().eq("id", entryId).first();
    if (entry) await entry.destroy();
  }
}

export type TournamentEntryDragData = {
  type: "tournament-entry";
  entry: TournamentEntry;
  matchId: string;
  round: number;
  entryIndex: number;
};

export function isTournamentEntryDragData(data: any): data is TournamentEntryDragData {
  return (
    data && typeof data === "object" && data.type === "tournament-entry" && TournamentEntry.is(data.entry)
  );
}

export default TournamentEntry;
